import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatInrWithoutDecimal } from "../../utils/formatWithoutDecimal";

const MONTHS = [
  { label: "Apr", calendarMonth: 4 },
  { label: "May", calendarMonth: 5 },
  { label: "Jun", calendarMonth: 6 },
  { label: "Jul", calendarMonth: 7 },
  { label: "Aug", calendarMonth: 8 },
  { label: "Sep", calendarMonth: 9 },
  { label: "Oct", calendarMonth: 10 },
  { label: "Nov", calendarMonth: 11 },
  { label: "Dec", calendarMonth: 12 },
  { label: "Jan", calendarMonth: 1 },
  { label: "Feb", calendarMonth: 2 },
  { label: "Mar", calendarMonth: 3 },
];

const LINE_COLORS = [
  "#2563EB", // blue
  "#F59E0B", // amber
  "#14B8A6", // teal
  "#8B5CF6", // violet
  "#EF4444", // red
  "#06B6D4", // cyan
  "#22C55E", // green
  "#F97316", // orange
  "#EC4899", // pink
  "#6366F1", // indigo
];

const CustomRevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        padding: "10px 12px",
        minWidth: "180px",
      }}
    >
      <div style={{ color: "#334155", fontSize: "0.85rem", marginBottom: "8px" }}>
        Sales ({label})
      </div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            color: entry.color,
            fontSize: "0.85rem",
            marginBottom: "4px",
            fontWeight: 500,
          }}
        >
          {entry.dataKey}: {formatInrWithoutDecimal(entry.value || 0)}
        </div>
      ))}
    </div>
  );
};
const formatAxisNumber = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 0,
  }).format(Math.trunc(n));
};

const Revenue = ({ className = "", revenueData: revenuePayload }) => {
  const [viewMode, setViewMode] = useState("monthly");
  const currentFYLabel = String(
    revenuePayload?.financial_year_label ||
      revenuePayload?.financial_year_wise?.[0]?.fiscal_year_label ||
      "",
  ).trim();
  const currentFYMonthwise = Array.isArray(
    revenuePayload?.monthwise_sales_current_financial_year,
  )
    ? revenuePayload.monthwise_sales_current_financial_year
    : [];

  const monthwiseByFY = {};

  // Supports flattened monthwise array where each row includes fiscal_year_label.
  const flattenedRows = Array.isArray(
    revenuePayload?.monthwise_sales_all_financial_years,
  )
    ? revenuePayload.monthwise_sales_all_financial_years
    : Array.isArray(revenuePayload?.monthwise_financial_year_wise)
    ? revenuePayload.monthwise_financial_year_wise
    : Array.isArray(revenuePayload?.monthwise_sales_financial_year_wise)
      ? revenuePayload.monthwise_sales_financial_year_wise
      : [];
  flattenedRows.forEach((row) => {
    const fyLabel = String(row?.fiscal_year_label || "").trim();
    const calMonth = Number(row?.calendar_month ?? row?.month);
    if (!fyLabel || !Number.isFinite(calMonth)) return;
    if (!monthwiseByFY[fyLabel]) monthwiseByFY[fyLabel] = {};
    monthwiseByFY[fyLabel][calMonth] = Number(
      row?.sales_total ?? row?.total_sales ?? row?.net_profit ?? 0,
    );
  });

  // Current FY monthwise fallback (present in your response).
  if (currentFYMonthwise.length > 0) {
    const label = currentFYLabel || "Current FY";
    if (!monthwiseByFY[label]) monthwiseByFY[label] = {};
    currentFYMonthwise.forEach((row) => {
      const calMonth = Number(row?.calendar_month);
      if (!Number.isFinite(calMonth)) return;
      monthwiseByFY[label][calMonth] = Number(
        row?.sales_total ?? row?.total_sales ?? row?.net_profit ?? 0,
      );
    });
  }

  let fyLabels = Object.keys(monthwiseByFY);

  // If backend later only sends yearly totals, show those FYs as flat lines.
  if (fyLabels.length === 0) {
    const yearlyRows = Array.isArray(revenuePayload?.financial_year_wise)
      ? revenuePayload.financial_year_wise
      : Array.isArray(revenuePayload?.net_profit_financial_year_wise)
        ? revenuePayload.net_profit_financial_year_wise
        : [];
    yearlyRows.forEach((row) => {
      const label = String(row?.fiscal_year_label || "").trim();
      if (!label) return;
      const val = Number(row?.sales_total ?? row?.total_sales ?? row?.net_profit ?? 0);
      monthwiseByFY[label] = Object.fromEntries(
        MONTHS.map((m) => [m.calendarMonth, val]),
      );
    });
    fyLabels = Object.keys(monthwiseByFY);
  }

  const selectedFYLabels = fyLabels.sort((a, b) => b.localeCompare(a));

  const chartData =
    selectedFYLabels.length > 0
      ? MONTHS.map((month) => {
          const row = { month: month.label };
          selectedFYLabels.forEach((fy) => {
            row[fy] = Number(monthwiseByFY[fy]?.[month.calendarMonth] ?? 0);
          });
          return row;
        })
      : MONTHS.map((month) => ({ month: month.label, "Current FY": 0 }));

  const lineKeys = selectedFYLabels.length > 0 ? selectedFYLabels : ["Current FY"];
  const yearlyRows = Array.isArray(revenuePayload?.financial_year_wise)
    ? revenuePayload.financial_year_wise
    : Array.isArray(revenuePayload?.net_profit_financial_year_wise)
      ? revenuePayload.net_profit_financial_year_wise
      : [];
  const yearlyChartData = useMemo(
    () =>
      yearlyRows
        .map((row) => ({
          label: String(row?.fiscal_year_label || row?.fiscal_year_start || ""),
          value: Number(row?.sales_total ?? row?.total_sales ?? row?.net_profit ?? 0),
        }))
        .filter((row) => row.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [yearlyRows],
  );
  const safeYearlyData =
    yearlyChartData.length > 0
      ? yearlyChartData
      : [{ label: currentFYLabel || "Current FY", value: 0 }];

  return (
    <div className={`col-lg-6 col-md-12 col-12 ${className}`.trim()}>
      <div
        className="card shadow-none h-100 border-0"
        style={{
          minHeight: "560px",
          borderRadius: "18px",
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
        }}
      >
        <div className="card-body pb-3">
          <div className="d-md-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-3">
              <div>
                <h5 className="card-title mb-1" style={{ color: "#0F172A" }}>
                  Revenue Forecast
                </h5>
                <p className="card-subtitle mb-0" style={{ color: "#64748B" }}>
                  {viewMode === "monthly"
                    ? "Month-wise Sales by Financial Year"
                    : "Year-wise Sales by Financial Year"}
                </p>
              </div>
            </div>
            <div
              className="d-flex align-items-center mt-3 mt-md-0"
              style={{
                border: "1px solid var(--theme-border, #E2E8F0)",
                borderRadius: "10px",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <button
                type="button"
                onClick={() => setViewMode("monthly")}
                style={{
                  border: "none",
                  padding: "5px 10px",
                  fontSize: "0.74rem",
                  fontWeight: 600,
                  color:
                    viewMode === "monthly"
                      ? "var(--theme-background, #FFFFFF)"
                      : "var(--theme-text-secondary, #334155)",
                  background:
                    viewMode === "monthly"
                      ? "var(--theme-primary, #2563EB)"
                      : "var(--theme-card-bg, #FFFFFF)",
                  cursor: "pointer",
                }}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setViewMode("yearly")}
                style={{
                  border: "none",
                  padding: "5px 10px",
                  fontSize: "0.74rem",
                  fontWeight: 600,
                  color:
                    viewMode === "yearly"
                      ? "var(--theme-background, #FFFFFF)"
                      : "var(--theme-text-secondary, #334155)",
                  background:
                    viewMode === "yearly"
                      ? "var(--theme-primary, #2563EB)"
                      : "var(--theme-card-bg, #FFFFFF)",
                  cursor: "pointer",
                }}
              >
                Yearly
              </button>
            </div>
          </div>

          <div style={{ height: "445px" }}>
            <ResponsiveContainer width="100%" height="100%">
              {viewMode === "monthly" ? (
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 8, left: 12, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#E2E8F0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#64748B", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    width={72}
                    domain={[0, "auto"]}
                    tick={{ fill: "#94A3B8", fontSize: 12 }}
                    tickFormatter={formatAxisNumber}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomRevenueTooltip />} />
                  <Legend />
                  {lineKeys.map((key, index) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={LINE_COLORS[index % LINE_COLORS.length]}
                      strokeWidth={2.5}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart
                  data={safeYearlyData}
                  margin={{ top: 8, right: 8, left: 12, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#E2E8F0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#64748B", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    width={72}
                    domain={[0, "auto"]}
                    tick={{ fill: "#94A3B8", fontSize: 12 }}
                    tickFormatter={formatAxisNumber}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(val) => [
                      formatInrWithoutDecimal(val || 0),
                      "Sales",
                    ]}
                    labelFormatter={(label) => `FY ${label}`}
                    cursor={false}
                    contentStyle={{
                      borderRadius: "10px",
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    radius={[6, 6, 0, 0]}
                    barSize={28}
                    isAnimationActive
                    animationDuration={750}
                  >
                    {safeYearlyData.map((row, idx) => (
                      <Cell
                        key={`yearly-bar-${row.label}`}
                        fill={
                          LINE_COLORS[
                            Math.max(
                              0,
                              lineKeys.findIndex((key) => key === row.label),
                            ) % LINE_COLORS.length
                          ]
                        }
                        opacity={row.label === currentFYLabel ? 1 : Math.max(0.65, 0.92 - idx * 0.05)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Revenue;
